import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import { ButtonBase, Stack, Typography, darken } from '@mui/material';
import config from 'config/default';
import { Genres } from 'enums';
import _ from 'lodash';
import React, { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { makeStyles } from 'tss-react/mui';
import PaiButton from '../ui/base-components/pai-button';
import PaiCheckbox from '../ui/base-components/pai-checkbox';
import PaiLink from '../ui/base-components/pai-link';
import PaiMenuItem from '../ui/base-components/pai-menu-item';
import PaiSelect from '../ui/base-components/pai-select';
import PaiTextArea from '../ui/base-components/pai-text-area';
import genresConfig, { TextGenerationCredentials } from './genres-config';

interface Props {
  hideTextGenerationInputs: boolean;
  hideTextGeneration: boolean;
  emptyEditor: boolean;

  selectedLanguage: string;
  isFormal: boolean;
  includeEmoji: boolean;

  genre: Genres | null;
  textGenerationCredentials: TextGenerationCredentials;

  textGenerationLoading: boolean;
  textAnalysisLoading: boolean;

  handleToggleTocAccepted?(): void;

  toggleHideTextGeneration(): void;

  toggleHideTextGenerationInputs(): void;

  handleSelectedLanguageChange(event: React.ChangeEvent<HTMLInputElement>): void;

  handleIsFormalChange(event: React.ChangeEvent<HTMLInputElement>): void;

  handleIncludeEmojiChange(event: React.ChangeEvent<HTMLInputElement>): void;

  handleTextGenerationCredentialsChange(event: React.ChangeEvent<HTMLInputElement>): void;

  handleGenerateText(): void;
}

const TextGenerationInputs: React.FC<Props> = ({
  hideTextGenerationInputs,
  hideTextGeneration,
  emptyEditor,
  selectedLanguage,
  isFormal,
  includeEmoji,
  genre,
  textGenerationCredentials,
  textGenerationLoading,
  textAnalysisLoading,
  handleToggleTocAccepted,
  toggleHideTextGeneration,
  toggleHideTextGenerationInputs,
  handleSelectedLanguageChange,
  handleIsFormalChange,
  handleIncludeEmojiChange,
  handleTextGenerationCredentialsChange,
  handleGenerateText,
}) => {
  const { classes, cx } = useStyles();
  const { t } = useTranslation();

  const hasEmptyField = useMemo(() => {
    for (const { genres, requiredFields } of genresConfig) {
      if (!_.isNil(genre) && genres.includes(genre)) {
        for (const field of requiredFields) {
          const value = _.get(textGenerationCredentials, field.valueKey);
          if (value === '' || value === undefined) {
            return true;
          }
        }
      }
    }
    return false;
  }, [genre, textGenerationCredentials]);

  return (
    <div className={cx(classes.column, hideTextGenerationInputs ? 'collapsed' : undefined)}>
      {!hideTextGeneration && !hideTextGenerationInputs ? (
        <div className={classes.textGenToggleContainer}>
          <PaiLink text={t('useOwnText')} size={'md'} underline={'always'} action={toggleHideTextGeneration} />
        </div>
      ) : null}
      {!emptyEditor ? (
        <ButtonBase className={classes.collapseButton} disableRipple={true} onClick={toggleHideTextGenerationInputs}>
          <ArrowBackIcon className={cx(classes.collapseIcon, hideTextGenerationInputs ? 'collapsed' : undefined)} />
        </ButtonBase>
      ) : undefined}
      {!hideTextGenerationInputs ? (
        <Stack gap={1} sx={{ height: '100%' }}>
          <Stack className={classes.textGenerationInputs} gap={2}>
            <PaiSelect value={selectedLanguage ?? ''} disableBorder={true} onChange={handleSelectedLanguageChange} disabled={textGenerationLoading}>
              {config.textGenerationLanguages.map((lang, index) => (
                <PaiMenuItem key={index} value={lang.key ?? ''}>
                  {t('language')}: {lang.text}
                </PaiMenuItem>
              ))}
            </PaiSelect>
            <Stack direction={'row'} justifyContent={'center'} alignItems={'center'} gap={2}>
              <PaiCheckbox checked={isFormal} label={t('politeSpeech')} onChange={handleIsFormalChange} disabled={textGenerationLoading} />
              <PaiCheckbox checked={includeEmoji} label={t('includeEmoji')} onChange={handleIncludeEmojiChange} disabled={textGenerationLoading} />
            </Stack>
            <Stack gap={1}>
              {genresConfig.map(({ genres, requiredFields }) =>
                !_.isNil(genre) && genres.includes(genre)
                  ? requiredFields.map((field) => (
                      <PaiTextArea
                        key={field.valueKey}
                        value={_.get(textGenerationCredentials, field.valueKey) || ''}
                        name={field.nameKey}
                        onChange={handleTextGenerationCredentialsChange}
                        instruction={field.instruction}
                        placeholder={field.placeholder}
                        rows={field.rows}
                        disableBorder={true}
                      />
                    ))
                  : null
              )}
              {genresConfig.map(({ genres, optionalFields }) =>
                !_.isNil(genre) && genres.includes(genre)
                  ? optionalFields.map((field) => (
                      <PaiTextArea
                        key={field.valueKey}
                        value={_.get(textGenerationCredentials, field.valueKey) || ''}
                        name={field.nameKey}
                        optional={true}
                        onChange={handleTextGenerationCredentialsChange}
                        instruction={field.instruction}
                        placeholder={field.placeholder}
                        rows={field.rows}
                        disableBorder={true}
                      />
                    ))
                  : null
              )}
            </Stack>
          </Stack>
          <Stack gap={1}>
            {genre === Genres.BLOG_POST || genre === Genres.LANDING_PAGE ? (
              <PaiLink
                text={t('useGeneratedToC')}
                variant={'primary'}
                underline={'always'}
                size={'md'}
                action={handleToggleTocAccepted}
                disabled={textAnalysisLoading || textGenerationLoading}
              />
            ) : null}
            <PaiButton
              text={t('generateTextVariants')}
              size={'large'}
              action={handleGenerateText}
              disabled={hasEmptyField || textAnalysisLoading || textGenerationLoading}
            />
          </Stack>
        </Stack>
      ) : (
        <ButtonBase className={classes.collapse} onClick={toggleHideTextGenerationInputs}>
          <Stack gap={2} alignItems={'center'} justifyContent={'flex-start'}>
            <Typography className={classes.collapseText}>{t('textGenerationInputs')}</Typography>
          </Stack>
        </ButtonBase>
      )}
    </div>
  );
};

const useStyles = makeStyles()((theme) => ({
  column: {
    position: 'relative',
    width: '350px',
    border: `2px solid ${theme.palette.backgrounds.main}`,
    padding: '16px 16px 8px 16px',
    '&.collapsed': {
      cursor: 'pointer',
      maxWidth: '56px',
      padding: 0,
      border: `2px solid ${darken('#FFF', 0.05)}`,
      [theme.breakpoints.down('xl')]: {
        maxWidth: 'unset',
        height: '56px',
      },
    },
    [theme.breakpoints.down('xl')]: {
      height: '640px',
      width: '100%',
    },
    [theme.breakpoints.down('lg')]: {
      height: 'unset',
      width: '100%',
    },
  },
  textGenToggleContainer: {
    position: 'absolute',
    top: '-28px',
    left: 0,
  },
  collapseButton: {
    position: 'absolute',
    top: '-32px',
    right: 0,
  },
  collapseIcon: {
    color: theme.palette.greys.medium,
    fontSize: theme.typography.pxToRem(20),
    '&.collapsed': {
      transform: 'rotate(180deg)',
    },
    '&:hover': {
      color: theme.palette.primary.main,
    },
  },
  textGenerationInputs: {
    height: 'calc(100% - 45px)',
    padding: '16px',
    overflow: 'auto',
  },
  collapse: {
    height: '100%',
    width: '100%',
    alignItems: 'flex-start',
    paddingTop: '16px',
    '&:hover': {
      background: darken('#FFF', 0.05),
    },
    [theme.breakpoints.down('lg')]: {
      paddingLeft: '16px',
      height: '56px',
    },
  },
  collapseText: {
    fontSize: theme.typography.pxToRem(15),
    fontWeight: 'bold',
    textTransform: 'uppercase',
    whiteSpace: 'nowrap',
    writingMode: 'vertical-lr',
    textOrientation: 'sideways',
    transform: 'rotate(180deg)',
    [theme.breakpoints.down('xl')]: {
      writingMode: 'unset',
      textOrientation: 'unset',
      transform: 'unset',
    },
  },
}));

export default TextGenerationInputs;
